import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Spinner, Modal, Form, Dropdown } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { IoIosLink } from "react-icons/io";
import { FaEye, FaDownload } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';

const API_BASE_URL = "https://backend.dlapakistan.com/api";

const MembersDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [links, setLinks] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [loadingLinks, setLoadingLinks] = useState(true);

  // DRAP Licence Modal State
  const [showDraModal, setShowDraModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    companyAddress: '',
    secpDocuments: null,
    soleProprietorshipDocs: null,
    companyLayoutPlan: null,
    cnicCopy: null,
    siteVerificationApp: '',
    isSoleProprietorship: false,
  });
  const [formErrors, setFormErrors] = useState({});

  // Manual Modal State
  const [showManualModal, setShowManualModal] = useState(false);
  const [pdfs, setPdfs] = useState([]);
  const [loadingPdfs, setLoadingPdfs] = useState(false);
  const [pdfsError, setPdfsError] = useState(null);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [showDropdown, setShowDropdown] = useState(false);

  const getAuthToken = () => localStorage.getItem('member_token');

  // Helper: fetch PDF blob with optional token
  const fetchPdfBlob = async (pdfId, useToken = true) => {
    const token = useToken ? getAuthToken() : null;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const response = await axios.get(`${API_BASE_URL}/members/pdfs/${pdfId}/download/`, {
        headers,
        responseType: 'blob'
      });
      if (response.data.type === 'application/pdf') {
        return response.data;
      } else {
        const text = await response.data.text();
        throw new Error(`Invalid response: ${text.substring(0, 100)}`);
      }
    } catch (error) {
      console.error(`Fetch failed (token=${useToken}):`, error);
      throw error;
    }
  };

  const handleOpenDraModal = () => {
    setFormData({
      ownerName: '',
      ownerEmail: '',
      ownerPhone: '',
      companyAddress: '',
      secpDocuments: null,
      soleProprietorshipDocs: null,
      companyLayoutPlan: null,
      cnicCopy: null,
      siteVerificationApp: '',
      isSoleProprietorship: false,
    });
    setFormErrors({});
    setShowDraModal(true);
  };

  const handleCloseDraModal = () => setShowDraModal(false);

  // Fetch PDFs with category filter
  const handleOpenManualModal = async (category = 'ALL') => {
    setSelectedCategory(category);
    setShowManualModal(true);
    setLoadingPdfs(true);
    setPdfsError(null);
    setPdfs([]);
    try {
      let url = `${API_BASE_URL}/members/pdfs/`;
      if (category !== 'ALL') {
        url += `?category=${category}`;
      }
      const response = await axios.get(url, {
        headers: { 'Authorization': '' }
      });
      if (Array.isArray(response.data)) {
        setPdfs(response.data);
      } else if (response.data.results && Array.isArray(response.data.results)) {
        setPdfs(response.data.results);
      } else {
        setPdfs([]);
        setPdfsError("Unexpected data format from server.");
      }
    } catch (error) {
      console.error("PDF fetch error:", error);
      let msg = "Failed to load documents. ";
      if (error.response) {
        msg += `Server error ${error.response.status}. ${error.response.data?.detail || ''}`;
      } else if (error.request) {
        msg += "No response from server. Is Django running?";
      } else {
        msg += error.message;
      }
      setPdfsError(msg);
    } finally {
      setLoadingPdfs(false);
    }
  };

  const handleCloseManualModal = () => {
    setShowManualModal(false);
    if (selectedPdf) {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
      setSelectedPdf(null);
      setPdfBlobUrl(null);
    }
  };

  // Handle dropdown selection
  const handleCategorySelect = (category) => {
    setShowDropdown(false);
    handleOpenManualModal(category);
  };

  // View PDF – try with token, then without token
  const handleViewPdf = async (pdf) => {
    setViewerLoading(true);
    try {
      let blob;
      if (getAuthToken()) {
        try {
          blob = await fetchPdfBlob(pdf.id, true);
        } catch (err) {
          console.warn("Token auth failed, trying without token...", err);
          blob = await fetchPdfBlob(pdf.id, false);
        }
      } else {
        blob = await fetchPdfBlob(pdf.id, false);
      }
      const blobUrl = URL.createObjectURL(blob);
      setPdfBlobUrl(blobUrl);
      setSelectedPdf(pdf);
    } catch (error) {
      console.error("Failed to load PDF", error);
      let errorMessage = "Could not load the document. ";
      if (error.response) {
        errorMessage += `Server responded with ${error.response.status}.`;
        if (error.response.status === 404) errorMessage += " The document may not exist.";
        else if (error.response.status === 403) errorMessage += " You don't have permission to view this document.";
        else if (error.response.status === 401) errorMessage += " Authentication failed. Please sign in again.";
      } else {
        errorMessage += error.message;
      }
      Swal.fire({
        icon: 'error',
        title: 'Cannot View PDF',
        text: errorMessage,
        confirmButtonColor: '#70B444'
      });
    } finally {
      setViewerLoading(false);
    }
  };

  const closePdfViewer = () => {
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setSelectedPdf(null);
    setPdfBlobUrl(null);
    setViewerLoading(false);
  };

  // Download PDF – similar fallback logic
  const handleDownloadPdf = async (pdf) => {
    try {
      let blob;
      if (getAuthToken()) {
        try {
          blob = await fetchPdfBlob(pdf.id, true);
        } catch (err) {
          console.warn("Token download failed, trying without token...", err);
          blob = await fetchPdfBlob(pdf.id, false);
        }
      } else {
        blob = await fetchPdfBlob(pdf.id, false);
      }
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `${pdf.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Download failed", error);
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: 'Could not download the document. Please try again.',
        confirmButtonColor: '#70B444'
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] || null }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
      if (name === 'isSoleProprietorship' && !checked) {
        setFormData(prev => ({ ...prev, soleProprietorshipDocs: null }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.ownerName.trim()) errors.ownerName = 'Owner name is required';
    if (!formData.ownerEmail.trim()) errors.ownerEmail = 'Owner email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.ownerEmail)) errors.ownerEmail = 'Email is invalid';
    if (!formData.ownerPhone.trim()) errors.ownerPhone = 'Owner phone is required';
    if (!formData.companyAddress.trim()) errors.companyAddress = 'Company address is required';
    if (!formData.cnicCopy) errors.cnicCopy = 'CNIC copy is required';
    if (formData.isSoleProprietorship && !formData.soleProprietorshipDocs) {
      errors.soleProprietorshipDocs = 'Sole Proprietorship documents are required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitDraForm = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);

    const formDataToSend = new FormData();
    formDataToSend.append('owner_name', formData.ownerName);
    formDataToSend.append('owner_email', formData.ownerEmail);
    formDataToSend.append('owner_phone', formData.ownerPhone);
    formDataToSend.append('company_address', formData.companyAddress);
    formDataToSend.append('is_sole_proprietorship', formData.isSoleProprietorship);
    formDataToSend.append('site_verification_app', formData.siteVerificationApp);

    if (formData.secpDocuments) formDataToSend.append('secp_documents', formData.secpDocuments);
    if (formData.soleProprietorshipDocs) formDataToSend.append('sole_proprietorship_docs', formData.soleProprietorshipDocs);
    if (formData.companyLayoutPlan) formDataToSend.append('company_layout_plan', formData.companyLayoutPlan);
    if (formData.cnicCopy) formDataToSend.append('cnic_copy', formData.cnicCopy);

    try {
      const response = await axios.post(`${API_BASE_URL}/members/drap/submit/`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        Swal.fire({ icon: 'success', title: 'Application Submitted', text: response.data.message, confirmButtonColor: '#70B444' });
        handleCloseDraModal();
      } else throw new Error(response.data.message || 'Submission failed');
    } catch (error) {
      console.error(error);
      let errorMessage = 'Failed to submit application. Please try again.';
      if (error.response?.data?.errors) errorMessage = Object.values(error.response.data.errors).flat().join('\n');
      else if (error.response?.data?.error) errorMessage = error.response.data.error;
      else if (error.response?.data?.message) errorMessage = error.response.data.message;
      else if (error.message) errorMessage = error.message;
      Swal.fire({ icon: 'error', title: 'Submission Failed', text: errorMessage, confirmButtonColor: '#70B444' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('member_token');
      if (token) await axios.post(`${API_BASE_URL}/members/logout/`, {}, { headers: { 'Authorization': `Bearer ${token}` } });
    } catch (error) { console.log(error); }
    localStorage.removeItem('member_token');
    localStorage.removeItem('member_data');
    delete axios.defaults.headers.common['Authorization'];
    Swal.fire({ icon: 'success', title: 'Signed Out', text: 'You have been signed out successfully.', timer: 2000, showConfirmButton: false });
    navigate('/members/members-area');
    setLoading(false);
  };

  const fetchMemberCards = async () => {
    try {
      setLoadingCards(true);
      const response = await axios.get(`${API_BASE_URL}/members-area/cards/`);
      let cardsData = [];
      if (Array.isArray(response.data)) cardsData = response.data;
      else if (response.data.data && Array.isArray(response.data.data)) cardsData = response.data.data;
      else if (response.data.results && Array.isArray(response.data.results)) cardsData = response.data.results;
      let activeCards = cardsData.filter(card => card.is_active === true);
      activeCards.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setCards(activeCards);
    } catch (error) {
      console.error("Error fetching cards:", error);
      setCards([]);
    } finally {
      setLoadingCards(false);
    }
  };

  const fetchMemberLinks = async () => {
    try {
      setLoadingLinks(true);
      const response = await axios.get(`${API_BASE_URL}/members-area/links/`);
      let linksData = [];
      if (Array.isArray(response.data)) linksData = response.data;
      else if (response.data.data && Array.isArray(response.data.data)) linksData = response.data.data;
      else if (response.data.results && Array.isArray(response.data.results)) linksData = response.data.results;
      let activeLinks = linksData.filter(link => link.is_active === true);
      activeLinks.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setLinks(activeLinks);
    } catch (error) {
      console.error("Error fetching links:", error);
      setLinks([]);
    } finally {
      setLoadingLinks(false);
    }
  };

  const memberData = localStorage.getItem('member_data');
  const member = memberData ? JSON.parse(memberData) : null;

  useEffect(() => {
    fetchMemberCards();
    fetchMemberLinks();
  }, []);

  const getImageUrl = (card) => {
    if (card.image_url) return card.image_url;
    if (card.image) {
      if (typeof card.image === 'string' && card.image.startsWith('http')) return card.image;
      return `https://backend.dlapakistan.com${card.image}`;
    }
    return 'https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80';
  };

  const handleCardButtonClick = (buttonLink) => {
    if (buttonLink) window.open(buttonLink, '_blank', 'noopener,noreferrer');
  };

  // Get category badge style - decent gray/green tones
  const getCategoryBadgeStyle = (category) => {
    switch(category) {
      case 'DRAP':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' };
      case 'HEALTHCARE':
        return { backgroundColor: '#e3f2fd', color: '#1565c0', border: '1px solid #bbdef5' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#616161', border: '1px solid #e0e0e0' };
    }
  };

  return (
    <Container fluid className="py-5 min-vh-100 mt-40 mx-auto" style={{ maxWidth: '80%' }}>
      <div className="d-flex justify-content-between align-items-center mb-5 flex-wrap gap-3">
        <div>
          <h1 className="display-4 text-dark">Members Area</h1>
          {member && <p className="text-muted mt-2">Welcome back, <strong>{member.first_name} {member.last_name}</strong></p>}
        </div>
        <div className="d-flex align-items-center gap-3">
          <Button variant="outline-none" onClick={handleSignOut} disabled={loading} className="px-4 py-2">
            {loading ? 'Signing Out...' : 'Sign Out'}
          </Button>
          <Button variant="link" onClick={() => navigate('/members/members-home')} className="fw-medium" style={{ color: '#70B444', textDecoration: 'none' }}>
            Manage Your Account
          </Button>

          {/* Dropdown for Manual button */}
          <Dropdown show={showDropdown} onToggle={setShowDropdown}>
            <Dropdown.Toggle
              style={{ backgroundColor: '#70B444', borderColor: '#70B444' }}
              className="fw-medium px-4 py-2"
              id="manual-dropdown"
            >
              Manual
            </Dropdown.Toggle>
            <Dropdown.Menu align="end" style={{ minWidth: '200px' }}>
              <Dropdown.Item
                onClick={() => handleCategorySelect('ALL')}
                className="d-flex align-items-center justify-content-between"
              >
                All Documents
                {selectedCategory === 'ALL' && <span className="text-success ms-2">✓</span>}
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={() => handleCategorySelect('DRAP')}
                className="d-flex align-items-center justify-content-between"
              >
                <div className="d-flex align-items-center">
                  <span className="d-inline-block me-2" style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#4caf50' }}></span>
                  DRAP Documents
                </div>
                {selectedCategory === 'DRAP' && <span className="text-success ms-2">✓</span>}
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => handleCategorySelect('HEALTHCARE')}
                className="d-flex align-items-center justify-content-between"
              >
                <div className="d-flex align-items-center">
                  <span className="d-inline-block me-2" style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#2196f3' }}></span>
                  Healthcare Documents
                </div>
                {selectedCategory === 'HEALTHCARE' && <span className="text-success ms-2">✓</span>}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <div className="bg-gray-100 p-4 mb-5 rounded shadow-sm text-lg" style={{ color: '#70B444' }}>
        Welcome to your DLA membership portal, {member ? member.company_name || 'your business' : 'your business'}. Explore your exclusive DLA membership benefits below:
      </div>

      {loadingCards ? (
        <div className="text-center py-5"><Spinner animation="border" style={{ color: '#70B444' }} /><p className="mt-3 text-muted">Loading exclusive content...</p></div>
      ) : cards.length === 0 ? (
        <div className="text-center py-5 bg-gray-50 rounded"><p className="text-muted mb-3">No content available yet</p><p className="small text-muted">Check back soon for exclusive member benefits</p></div>
      ) : (
        <Row className="g-4">
          {cards.map((card) => (
            <Col lg={3} md={6} sm={12} key={card.id}>
              <div className="border-2 border-gray-200 h-100 d-flex flex-column text-center bg-white shadow hover:shadow-md transition-all" style={{ minHeight: '380px' }}>
                <img src={getImageUrl(card)} alt={card.title} className="mx-auto" style={{ maxWidth: '18.2vw', height: 'auto', objectFit: 'cover' }} onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'; }} />
                <div className="flex-grow-1 d-flex flex-column p-4 pt-0" style={{ backgroundColor: '#70B444', color: 'white' }}>
                  <h5 className="fw-bold mt-4 mb-2">{card.title}</h5>
                  <hr className="border-white mb-3 w-64 mx-auto opacity-75" />
                  <Button variant="light" size="sm" className="w-100 mt-auto fw-medium" style={{ color: '#70B444', border: 'none' }} onClick={() => handleCardButtonClick(card.button_link)}>
                    {card.button_text || 'Find Out More'}
                  </Button>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}

      <div className="mt-12 bg-gray-100 p-6">
        <h2 className="text-[26px] text-[#70B444] font-normal mb-5">Need extra advice and information? Explore our recommended external links:</h2>
        {loadingLinks ? (
          <div className="text-center py-5"><Spinner animation="border" style={{ color: '#70B444' }} /><p className="mt-3 text-muted">Loading external links...</p></div>
        ) : links.length === 0 ? (
          <div className="text-center py-5"><p className="text-muted">No external links available yet. Check back soon!</p></div>
        ) : (
          links.map((link, index) => (
            <div key={link.id}>
              <h3 className="text-[20px] text-[#70B444] font-normal mb-3">{link.title}</h3>
              {link.description && <p className="text-gray-700 mb-4">{link.description}</p>}
              <a href={link.link_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#70B444] text-white px-4 py-2 rounded-none mb-6" style={{ textDecoration: 'none' }}>
                <IoIosLink size={14} /> {link.button_text || 'Visit Website'}
              </a>
              {index < links.length - 1 && <div className="border-b border-gray-300 mb-6"></div>}
            </div>
          ))
        )}
      </div>

      <div className="py-4 px-2">
        <h3 className="text-[#70B444] mb-3">Legal Disclaimer</h3>
        <p className="text-gray-700 mb-4">The contents of this site are for general information purposes only. Whilst the Dental Laboratories Associations (DLA) endeavour to ensure that the information on this website is correct, no warranty, express or implied, is given as to its accuracy and we do not accept any liability for error or omission. Included on this website (members recourses area) the DLA provides a library of written business advice documents and templates, the DLA strongly recommends you seek professional advice when editing these documents for either your personal or business use. The DLA shall not be liable for any damage (including, without limitation, damage for loss of business or loss of profits) arising in contract, tort or otherwise from the use of, or inability to use, this site or any material contained in it, or from any action or decision taken as a result of using this site or any such material.</p>
        <p className="text-gray-700 mb-4">Some of the material on this site may have been prepared some time ago. Please contact us if you need a comprehensive and up-to-date statement of the relevant product or service.</p>
      </div>

      {/* DRAP Licence Modal */}
      <Modal show={showDraModal} onHide={handleCloseDraModal} size="lg" centered>
        <Modal.Header closeButton style={{ borderBottom: `2px solid #70B444`, padding: '1.5rem 2rem' }}>
          <Modal.Title className="text-[#70B444] fw-bold">DRAP Licence Application</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitDraForm}>
          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', padding: '2rem' }}>
            <p className="text-muted mb-4">Please fill out the following information to apply for your DRAP licence. Required fields are marked with *</p>
            <Row>
              <Col md={6}>
                <h5 className="text-[#70B444] mb-3">Company Owner Details *</h5>
                <Form.Group className="mb-4"><Form.Label>Full Name *</Form.Label><Form.Control type="text" name="ownerName" value={formData.ownerName} onChange={handleInputChange} isInvalid={!!formErrors.ownerName} placeholder="Enter owner's full name" /><Form.Control.Feedback type="invalid">{formErrors.ownerName}</Form.Control.Feedback></Form.Group>
                <Form.Group className="mb-4"><Form.Label>Email Address *</Form.Label><Form.Control type="email" name="ownerEmail" value={formData.ownerEmail} onChange={handleInputChange} isInvalid={!!formErrors.ownerEmail} placeholder="owner@example.com" /><Form.Control.Feedback type="invalid">{formErrors.ownerEmail}</Form.Control.Feedback></Form.Group>
                <Form.Group className="mb-4"><Form.Label>Phone Number *</Form.Label><Form.Control type="tel" name="ownerPhone" value={formData.ownerPhone} onChange={handleInputChange} isInvalid={!!formErrors.ownerPhone} placeholder="+44 1234 567890" /><Form.Control.Feedback type="invalid">{formErrors.ownerPhone}</Form.Control.Feedback></Form.Group>
                <h5 className="text-[#70B444] mb-3">Company Address *</h5>
                <Form.Group className="mb-4"><Form.Label>Full Address *</Form.Label><Form.Control as="textarea" rows={2} name="companyAddress" value={formData.companyAddress} onChange={handleInputChange} isInvalid={!!formErrors.companyAddress} placeholder="Street, City, Postcode, Country" /><Form.Control.Feedback type="invalid">{formErrors.companyAddress}</Form.Control.Feedback></Form.Group>
                <h5 className="text-[#70B444] mb-3">CNIC Copy *</h5>
                <Form.Group className="mb-4"><Form.Label>Upload CNIC Copy *</Form.Label><Form.Control type="file" name="cnicCopy" onChange={handleInputChange} accept=".pdf,.jpg,.png" isInvalid={!!formErrors.cnicCopy} required /><Form.Control.Feedback type="invalid">{formErrors.cnicCopy}</Form.Control.Feedback><Form.Text className="text-muted">Please upload a clear copy of your CNIC (front and back if possible)</Form.Text></Form.Group>
              </Col>
              <Col md={6}>
                <h5 className="text-[#70B444] mb-3">SECP Documents</h5>
                <Form.Group className="mb-4"><Form.Label>Upload SECP Documents (Optional)</Form.Label><Form.Control type="file" name="secpDocuments" onChange={handleInputChange} accept=".pdf,.doc,.docx,.jpg,.png" /><Form.Text className="text-muted">Supported formats: PDF, DOC, DOCX, JPG, PNG</Form.Text></Form.Group>
                <Form.Group className="mb-4"><Form.Check type="checkbox" label="Is this a Sole Proprietorship business?" name="isSoleProprietorship" checked={formData.isSoleProprietorship} onChange={handleInputChange} /></Form.Group>
                {formData.isSoleProprietorship && (<Form.Group className="mb-4"><Form.Label>Sole Proprietorship Documents *</Form.Label><Form.Control type="file" name="soleProprietorshipDocs" onChange={handleInputChange} accept=".pdf,.doc,.docx,.jpg,.png" isInvalid={!!formErrors.soleProprietorshipDocs} /><Form.Control.Feedback type="invalid">{formErrors.soleProprietorshipDocs}</Form.Control.Feedback><Form.Text className="text-muted">Please upload relevant Sole Proprietorship documents (e.g., registration proof)</Form.Text></Form.Group>)}
                <h5 className="text-[#70B444] mb-3">Company Layout Plan</h5>
                <Form.Group className="mb-4"><Form.Label>Upload Layout Plan (Optional)</Form.Label><Form.Control type="file" name="companyLayoutPlan" onChange={handleInputChange} accept=".pdf,.doc,.docx,.jpg,.png" /><Form.Text className="text-muted">Upload your company's layout or floor plan (optional)</Form.Text></Form.Group>
                <h5 className="text-[#70B444] mb-3">Site Verification Application</h5>
                <Form.Group className="mb-2"><Form.Label>Application URL (Optional)</Form.Label><Form.Control type="url" name="siteVerificationApp" value={formData.siteVerificationApp} onChange={handleInputChange} placeholder="https://example.com/verification" /><Form.Text className="text-muted">If you have an online site verification application, please provide the link</Form.Text></Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer style={{ borderTop: `1px solid #dee2e6`, padding: '1.25rem 2rem' }}>
            <Button variant="secondary" onClick={handleCloseDraModal}>Cancel</Button>
            <Button type="submit" disabled={submitting} style={{ backgroundColor: '#70B444', borderColor: '#70B444' }}>{submitting ? 'Submitting...' : 'Submit Application'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Manual Modal - with category info */}
      <Modal show={showManualModal} onHide={handleCloseManualModal} size="lg" centered>
        <Modal.Header closeButton style={{ borderBottom: `2px solid #70B444`, padding: '1.5rem 2rem' }}>
          <Modal.Title className="text-[#70B444] fw-bold">
            Procedure Documents
            {selectedCategory !== 'ALL' && (
              <span
                className="ms-3 px-3 py-1 rounded-pill fs-6 fw-normal"
                style={getCategoryBadgeStyle(selectedCategory)}
              >
                {selectedCategory === 'DRAP' ? 'DRAP Documents' : 'Healthcare Documents'}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', padding: '2rem' }}>
          {loadingPdfs ? (
            <div className="text-center py-5"><Spinner animation="border" style={{ color: '#70B444' }} /><p className="mt-3 text-muted">Loading documents...</p></div>
          ) : pdfsError ? (
            <div className="alert alert-danger" role="alert">
              {pdfsError}
              <div className="mt-3"><Button variant="outline-danger" onClick={() => handleOpenManualModal(selectedCategory)}>Retry</Button></div>
            </div>
          ) : pdfs.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">
                {selectedCategory === 'ALL'
                  ? 'No procedure documents available yet.'
                  : `No ${selectedCategory === 'DRAP' ? 'DRAP' : 'Healthcare'} documents available yet.`
                }
              </p>
            </div>
          ) : (
            <div className="pdf-list">
              {pdfs.map((pdf) => (
                <div key={pdf.id} className="pdf-card mb-3 p-3 border rounded shadow-sm bg-white d-flex align-items-center justify-content-between hover-shadow transition">
                  <div className="pdf-info d-flex align-items-center flex-grow-1">
                    <div className="pdf-icon me-3 text-[#70B444]">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 4H20V20H4V4Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                        <path d="M8 8H16M8 12H16M8 16H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <h6 className="mb-0 fw-semibold text-dark">{pdf.name}</h6>
                        {pdf.category && (
                          <span
                            className="px-2 py-0.5 rounded-pill fs-6"
                            style={getCategoryBadgeStyle(pdf.category)}
                          >
                            {pdf.category_display || pdf.category}
                          </span>
                        )}
                      </div>
                      <small className="text-muted">Uploaded: {pdf.uploaded_at_formatted || new Date(pdf.uploaded_at).toLocaleDateString()}</small>
                    </div>
                  </div>
                  <div className="pdf-actions d-flex gap-2 ms-3">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleViewPdf(pdf)}
                      title="View PDF"
                      className="d-flex align-items-center justify-content-center"
                      style={{ width: '36px', height: '36px', borderRadius: '8px' }}
                    >
                      <FaEye />
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleDownloadPdf(pdf)}
                      title="Download PDF"
                      className="d-flex align-items-center justify-content-center"
                      style={{ width: '36px', height: '36px', borderRadius: '8px' }}
                    >
                      <FaDownload />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseManualModal}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* PDF Viewer Modal */}
      {selectedPdf && (
        <Modal show={!!selectedPdf} onHide={closePdfViewer} size="xl" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedPdf.name}
              {selectedPdf.category && (
                <span
                  className="ms-3 px-3 py-1 rounded-pill fs-6 fw-normal"
                  style={getCategoryBadgeStyle(selectedPdf.category)}
                >
                  {selectedPdf.category_display || selectedPdf.category}
                </span>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ height: '80vh', padding: 0, position: 'relative' }}>
            {viewerLoading ? (
              <div className="d-flex justify-content-center align-items-center h-100">
                <Spinner animation="border" style={{ color: '#70B444' }} />
                <span className="ms-3">Loading PDF...</span>
              </div>
            ) : pdfBlobUrl ? (
              <iframe
                src={pdfBlobUrl}
                title={selectedPdf.name}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            ) : (
              <div className="d-flex justify-content-center align-items-center h-100 text-danger">
                Failed to load PDF.
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closePdfViewer}>Close</Button>
            <Button
              variant="success"
              onClick={() => handleDownloadPdf(selectedPdf)}
              style={{ backgroundColor: '#70B444', borderColor: '#70B444' }}
            >
              Download
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <style jsx>{`
        .pdf-card {
          transition: all 0.2s ease-in-out;
        }
        .pdf-card:hover {
          box-shadow: 0 0.5rem 1rem rgba(0,0,0,0.1) !important;
          transform: translateY(-2px);
        }
        .transition {
          transition: all 0.2s;
        }
      `}</style>
    </Container>
  );
};

export default MembersDashboard;